import { useEffect, useRef } from 'react';

// How many modals currently hold the app root inert. Ref-counted rather than a
// bare boolean so that if two modals are ever open at once (a future confirm over
// an editor), closing the inner one does not un-inert the background while the
// outer is still open — inert clears only when the last modal closes.
let inertHolders = 0;

// Shared dialog behaviour for the app's modals: focus the dialog once on open,
// trap Tab within it, close on Escape, and restore focus to the opener on close.
//
// The focus-on-open MUST run exactly once — a previous per-modal version depended
// on `onClose`, which callers pass inline (a new identity every render), so any
// unrelated parent re-render (a WebSocket frame, a save-status change) re-ran the
// effect and yanked focus back to the dialog mid-interaction. Here `onClose` is
// read through a ref so the Escape handler always calls the latest without making
// the effect re-run.
export default function useModalDismiss(onClose) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const dialog = dialogRef.current;
    dialog?.focus();

    // Make everything behind the modal inert while it is open, so a keyboard user
    // or AT in browse mode cannot reach the backdrop content. The dialog is
    // portaled to <body> (see ModalShell), a sibling of #root, so inerting #root
    // covers the whole app without touching the dialog itself. #root is absent in
    // unit tests (Testing Library mounts into a bare container) → this is a no-op
    // there, and the Tab trap below still contains focus.
    const appRoot = document.getElementById('root');
    inertHolders += 1;
    if (appRoot) {
      appRoot.inert = true;
    }

    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key === 'Tab' && dialog) {
        const focusable = dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) {
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      inertHolders = Math.max(0, inertHolders - 1);
      if (appRoot && inertHolders === 0) {
        appRoot.inert = false;
      }
      // Return focus to whatever opened the modal, so keyboard/SR users are not
      // dropped onto <body> when it unmounts. If the opener has itself been
      // unmounted in the same commit (e.g. a confirm whose action removes the
      // button that opened it), it is detached and focusing it would drop to
      // <body> — fall back to the screen heading (id="main-content").
      const restoreTarget =
        opener && opener.isConnected
          ? opener
          : document.getElementById('main-content');
      restoreTarget?.focus?.();
    };
    // Intentionally run once: onClose is read via a ref (see comment above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return dialogRef;
}
